import { Loader2, Lock, X } from 'lucide-react'
import type { OpenPermitListItem } from 'modules/permit/api/permitService'
import { Button } from 'shared/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Label } from 'shared/ui/label'
import { Separator } from 'shared/ui/separator'
import { Textarea } from 'shared/ui/textarea'
import { formatDateRange, formatTime } from './utils'

interface ClosePermitDialogProps {
  open: boolean
  permits: OpenPermitListItem[]
  summary: string
  isSubmitting: boolean
  onOpenChange: (open: boolean) => void
  onSummaryChange: (value: string) => void
  onSubmit: () => void
}

export function ClosePermitDialog({
  open,
  permits,
  summary,
  isSubmitting,
  onOpenChange,
  onSummaryChange,
  onSubmit,
}: ClosePermitDialogProps) {
  const isMassClose = permits.length > 1
  const primaryPermit = permits[0]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl gap-0 p-0" showCloseButton={false}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
              <Lock className="h-5 w-5 text-rose-500" />
              {'\u0e1b\u0e34\u0e14 Permit'}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {isMassClose
                ? `\u0e15\u0e23\u0e27\u0e08\u0e2a\u0e2d\u0e1a\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25\u0e41\u0e25\u0e30\u0e1a\u0e31\u0e19\u0e17\u0e36\u0e01\u0e01\u0e32\u0e23\u0e1b\u0e34\u0e14 Permit \u0e2a\u0e33\u0e2b\u0e23\u0e31\u0e1a ${permits.length} \u0e23\u0e32\u0e22\u0e01\u0e32\u0e23`
                : '\u0e15\u0e23\u0e27\u0e08\u0e2a\u0e2d\u0e1a\u0e01\u0e32\u0e23\u0e1b\u0e0f\u0e34\u0e1a\u0e31\u0e15\u0e34\u0e07\u0e32\u0e19\u0e41\u0e25\u0e30\u0e40\u0e04\u0e25\u0e35\u0e22\u0e23\u0e4c\u0e1e\u0e37\u0e49\u0e19\u0e17\u0e35\u0e48\u0e01\u0e48\u0e2d\u0e19\u0e1b\u0e34\u0e14 Permit'}
            </DialogDescription>
          </DialogHeader>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full text-slate-500 hover:bg-slate-100"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {isMassClose ? (
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-6">
              <div className="space-y-3">
                <p className="text-lg font-semibold text-slate-900">{'\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25 Permit'}</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {permits.slice(0, 6).map((permit) => (
                    <div key={permit.permitId} className="rounded-xl border border-white/80 bg-white/70 p-3 text-sm text-slate-700">
                      <p className="font-semibold text-slate-900">{permit.permitNumber || permit.permitId}</p>
                      <p className="mt-1">{permit.workDescription || permit.title || '-'}</p>
                    </div>
                  ))}
                </div>
                {permits.length > 6 ? (
                  <p className="text-sm text-slate-500">{`\u0e41\u0e25\u0e30\u0e2d\u0e35\u0e01 ${permits.length - 6} \u0e23\u0e32\u0e22\u0e01\u0e32\u0e23`}</p>
                ) : null}
              </div>
            </div>
          ) : primaryPermit ? (
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-6">
              <p className="mb-5 text-lg font-semibold text-slate-900">{'\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25 Permit :'}</p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid grid-cols-[140px,1fr] gap-y-4 text-sm">
                  <span className="font-medium text-blue-700">Permit No.:</span>
                  <span className="font-semibold text-slate-900">
                    {primaryPermit.permitNumber || primaryPermit.permitId}
                    {primaryPermit.permitType ? ` (${primaryPermit.permitType})` : ''}
                  </span>

                  <span className="font-medium text-blue-700">{'\u0e1c\u0e39\u0e49\u0e02\u0e2d\u0e2d\u0e19\u0e38\u0e0d\u0e32\u0e15:'}</span>
                  <span className="font-semibold text-slate-900">
                    {primaryPermit.requesterName || '-'}
                    {primaryPermit.requesterCompany ? ` (${primaryPermit.requesterCompany})` : ''}
                  </span>
                </div>

                <div className="grid grid-cols-[140px,1fr] gap-y-4 text-sm">
                  <span className="font-medium text-blue-700">{'\u0e23\u0e32\u0e22\u0e25\u0e30\u0e40\u0e2d\u0e35\u0e22\u0e14\u0e07\u0e32\u0e19 :'}</span>
                  <span className="font-semibold text-slate-900">{primaryPermit.workDescription || primaryPermit.title || '-'}</span>

                  <span className="font-medium text-blue-700">วันที่เข้าปฏิบัติงาน :</span>
                  <span className="font-semibold text-slate-900">
                    {formatDateRange(primaryPermit.plannedStart, primaryPermit.plannedEnd)}
                  </span>

                  <span className="font-medium text-blue-700">เวลา :</span>
                  <span className="font-semibold text-slate-900">
                    {formatTime(primaryPermit.plannedStart)} - {formatTime(primaryPermit.plannedEnd)}
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          <Separator />

          <div className="space-y-3">
            <Label htmlFor="close-summary" className="text-base font-semibold text-slate-900">
              {'\u0e2b\u0e21\u0e32\u0e22\u0e40\u0e2b\u0e15\u0e38\u0e01\u0e32\u0e23\u0e1b\u0e34\u0e14 Permit'}
            </Label>
            <Textarea
              id="close-summary"
              rows={5}
              className="min-h-[120px] resize-none rounded-2xl border-slate-300 text-base placeholder:text-slate-400"
              placeholder={'\u0e23\u0e30\u0e1a\u0e38\u0e2b\u0e21\u0e32\u0e22\u0e40\u0e2b\u0e15\u0e38\u0e40\u0e1e\u0e34\u0e48\u0e21\u0e40\u0e15\u0e34\u0e21 \u0e40\u0e0a\u0e48\u0e19 \u0e1b\u0e31\u0e0d\u0e2b\u0e32\u0e17\u0e35\u0e48\u0e1e\u0e1a, \u0e02\u0e49\u0e2d\u0e2a\u0e31\u0e07\u0e40\u0e01\u0e15, \u0e04\u0e33\u0e41\u0e19\u0e30\u0e19\u0e33...'}
              value={summary}
              onChange={(event) => onSummaryChange(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {'\u0e22\u0e01\u0e40\u0e25\u0e34\u0e01'}
          </Button>
          <Button className="bg-rose-500 text-white hover:bg-rose-600" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
            {'\u0e22\u0e37\u0e19\u0e22\u0e31\u0e19\u0e1b\u0e34\u0e14 Permit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
